import { mapValues } from 'lodash-es'
import { useMemo } from 'react'
import type { GenerationModelType } from 'src/services/apollo/generated/graphql'
import {
  type ModelFeatures,
  isModelFeatureEnabled,
  useModelFeatures,
} from 'src/services/orpc/modelFeature'
import { useModelVersion } from 'src/stores/model'
import {
  getBaseLoraTypeFromModelVersion,
  modelSupportsControlNet,
  isChatModel,
  isLoraCompatWithModel,
  isModelCompatibleWithIPAdapter,
  isSDV1Model,
} from 'src/utils/model'
import {
  isCnetsCompatWithIPAdapter,
  isCnetsCompatWithModelType,
} from 'src/utils/task'
import type { TaskSubmitFormValues } from '../BaseTaskSubmitForm'

export const VALIDATING = Symbol('Validating')

// oxfmt-ignore
type ValidationError<P extends Record<string | number | symbol, unknown>> = Record<keyof P, symbol | string[]>
// oxfmt-ignore
export type ValidateTaskParamsResult<P extends Record<string | number | symbol, unknown>> = ValidationError<P> & { ok: boolean }

interface ValidateTaskParamsContext {
  modelType?: GenerationModelType
  loraTypes?: Record<string, GenerationModelType | undefined>
  modelFeatures?: ModelFeatures
  versionExtra?: Record<string, any>
}

export function validateTaskParams<P extends TaskSubmitFormValues>(
  params: P,
  context: ValidateTaskParamsContext,
): ValidateTaskParamsResult<P> {
  const error = {} as ValidationError<P>

  function add(key: keyof P, reason: string | symbol) {
    if (typeof reason === 'symbol') {
      error[key] = reason
    } else if (typeof error[key] !== 'symbol') {
      error[key] ??= []
      ;(error[key] as string[]).push(reason)
    }
  }

  function result() {
    return { ...error, ok: Object.keys(error).length === 0 }
  }

  /*
   * ---
   */

  if (params.modelId && !context.modelType) {
    add('modelId', VALIDATING)
    return result()
  }

  if (isChatModel(context.modelType)) {
    if (!params.prompts?.trim()) {
      add('prompts', 'Prompts are required')
    }
    if (!params.multiImageReference?.referenceImages?.length) {
      add('multiImageReference', 'Reference image is required')
    }
    return result()
  }

  const usingSDv1Model = isSDV1Model(context.modelType)

  const loraIds = Object.keys(params.lora ?? {})

  if (!context.modelType || loraIds.some(id => !context.loraTypes?.[id])) {
    add('lora', VALIDATING)
  } else {
    if (loraIds.length > 0) {
      if (!isModelFeatureEnabled(context.modelFeatures, 'lora', true)) {
        add('lora', 'The chosen model does not support Lora')
      } else if (
        // Every lora must be compatible with the model
        !Object.values(context.loraTypes!).every(type =>
          isLoraCompatWithModel(context.modelType!, type),
        )
      ) {
        add('lora', 'Some chosen loras are not compatible with the model')
      }
    }
  }

  if (!usingSDv1Model && params.latentCouple) {
    add('latentCouple', 'Non-SDv1 model does not support composition')
  }

  if (!usingSDv1Model && params.enableTile) {
    add('enableTile', 'Non-SDv1 model does not support ultimate upscale')
  }

  if (
    (params.controlNets ?? []).length > 0 &&
    (!modelSupportsControlNet(context.versionExtra) ||
      !isCnetsCompatWithModelType(params.controlNets!, context.modelType!))
  ) {
    add('controlNets', 'The chosen model does not support ControlNet')
  }

  if (params.ipAdapter?.enabled) {
    if (!params.ipAdapter?.imagesAreValid) {
      add('ipAdapter', 'Not all images are valid')
    } else if (
      (params.controlNets ?? []).length > 0 &&
      !isCnetsCompatWithIPAdapter(params.controlNets!)
    ) {
      add('controlNets', 'The chosen ControlNet does not support Character')
    } else if (!isModelCompatibleWithIPAdapter(context.modelType)) {
      add('ipAdapter', 'The chosen model does not support Character')
    }
  }

  // Be ready for the future support of custom VAE
  // if (usingXLModel && params.vaeModeId) {
  //   add('vaeModeId', 'XL model does not support VAE')
  // }

  // Be ready for the future support of dynamic CFG
  // if (usingXLModel && params.dynthres) {
  //   add('dynthres', 'XL model does not support dynamic CFG')
  // }

  // Be ready for the future support of AnimateDiff
  // if (usingXLModel && params.animateDiff) {
  //   add('animateDiff', 'XL model does not support AnimateDiff')
  // }

  return result()
}

export function useValidateTaskParams(params: TaskSubmitFormValues) {
  const modelVersion = useModelVersion(params.modelId)
  const { data: modelFeaturesData } = useModelFeatures(params.modelId)
  const keys = useMemo(() => Object.keys(params.lora ?? {}), [params.lora])
  const loraVersions = useModelVersion(keys)

  return useMemo(
    () =>
      validateTaskParams(params, {
        modelType: modelVersion?.modelType,
        loraTypes: mapValues(loraVersions, version =>
          getBaseLoraTypeFromModelVersion(version),
        ),
        modelFeatures: modelFeaturesData?.features,
        versionExtra: modelVersion?.extra,
      }),
    [params, modelVersion, loraVersions, modelFeaturesData?.features],
  )
}
